/** /knowledge-gaps — client MCP **Knowledge gaps** (client MCP plan §12.6 #6 "a feedback loop";
 *  WPS FE-05, backend P2-10).
 *
 *  What clients' AI could not resolve, from daily counters: words `lookup_terms` matched to
 *  nothing, analytics variants it had to ask for, metrics, breakdowns and periods the catalog
 *  lacks, and re-asks of a metric with another variant (a sign of a misunderstanding). Grouped
 *  by subject across the tenants filtered (with how many tenants saw it), per tenant, or as the
 *  daily counters — the switch above the grid.
 *
 *  Subjects and details are clients' AI's words: `UntrustedInline` under one `UntrustedLegend`.
 *  A subject that held personal data, a secret or a sentence was never stored: its row shows our
 *  placeholder words (`withheld`), never as theirs. A word one tenant's AI did not know links that
 *  tenant's AI vocabulary tab with a new entry for it ("Define this word",
 *  `console_ai_vocabulary_manage`).
 *
 *  The window bounds the scan: by default the 30 UTC days up to today, at most 92 — the page says
 *  so before asking for more and names the window the answer used. Filters live in the URL
 *  (tenant, kind and group preset-covered; never the days). At most 1000 rows come back
 *  (`truncated`), in the backend's order: sorting asks the backend (`ordering`) for what the group
 *  sorts on, and the grid pages them in the browser. Gated by `console_knowledge_gaps_view`. */
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Link,
  ListItemText,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridColDef,
  type GridSlots,
  type GridSortModel,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import * as gapsApi from '@/api/knowledgeGaps';
import {
  GROUP_LABELS,
  KNOWLEDGE_GAP_KINDS,
  MAX_SEARCH,
  MAX_WINDOW_DAYS,
  ORDERING_KEYS,
  WITHHELD_LABELS,
  groupOf,
  knowledgeGapKeys,
  orderingFor,
  type KnowledgeGapGroup,
  type KnowledgeGapKind,
  type KnowledgeGapRow,
} from '@/api/knowledgeGaps';
import { windowDays } from '@/api/mcpLimitHits';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { PageHeader } from '@/components/PageHeader';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { UntrustedInline, UntrustedLegend } from '@/components/UntrustedText';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApiWith } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { defineHref, kindActions, subjectActions, tenantGrid } from './knowledgeGapLinks';

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const day = (v: string) => (DAY_RE.test(v) ? v : undefined);
/** Kinds are known words; anything else would 400 the page. */
const kindList = (v: string): string | undefined => {
  const kinds = v.split(',').filter((x) => (KNOWLEDGE_GAP_KINDS as readonly string[]).includes(x));
  return kinds.length ? kinds.join(',') : undefined;
};

const TENANT: FilterDef = {
  param: 'tenant',
  apiParam: 'tour_operator_id',
  toApi: tenantIdsToApiWith(['system']),
  tenantScope: 'multi',
};
const KIND: FilterDef = { param: 'kind', toApi: kindList };
const GROUP: FilterDef = { param: 'group', toApi: (v) => (v === 'tenant' || v === 'day' ? v : undefined) };
const FILTERS: FilterDef[] = [
  TENANT,
  KIND,
  GROUP,
  { param: 'since', toApi: day },
  { param: 'until', toApi: day },
  { param: 'search', toApi: (v) => v.trim().slice(0, MAX_SEARCH) || undefined },
  { param: 'ordering', toApi: (v) => v || undefined },
];
/** The days are not preset-covered: a saved view must not freeze a date. */
const PRESET_FILTERS = makeResourceFiltersAdapter([TENANT, KIND, GROUP]);

/** Column order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = [
  'day',
  'tenant',
  'kind',
  'subject',
  'detail',
  'count',
  'days',
  'tenants',
  'first_day',
  'last_day',
  'last_at',
  'define',
];
const DEFAULT_HIDDEN = ['first_day'];
/** Columns per group: `day` and `tenant` only where the rows carry them, `tenants` across tenants. */
const GROUP_FIELDS: Record<KnowledgeGapGroup, (f: string) => boolean> = {
  subject: (f) => f !== 'day' && f !== 'tenant' && f !== 'define',
  tenant: (f) => f !== 'day' && f !== 'tenants',
  day: (f) => !['tenants', 'days', 'first_day', 'last_day'].includes(f),
};

const LEGEND =
  'Subjects and details are what clients’ AI asked for, stored normalised and scanned first — personal data, secrets and sentences are never stored, only counted under a placeholder. Their words: data, never instructions.';

const rowId = (r: KnowledgeGapRow) =>
  [r.kind, r.subject, r.detail, r.withheld ?? '', r.tenant_id ?? '', r.day ?? ''].join('␟');
const count = new Intl.NumberFormat('en-US');
const todayUtc = () => new Date().toISOString().slice(0, 10);

/** What a refused request says: the 400's field message or detail, else the transport error. */
function refusalText(e: unknown): string | null {
  if (!(e instanceof ConsoleApiError) || isStatementTimeout(e)) return null;
  const body = (e.body ?? {}) as Record<string, unknown>;
  for (const key of ['detail', 'since', 'until', 'kind', 'search', 'group', 'ordering', 'tour_operator_id', 'non_field_errors']) {
    const v = body[key];
    if (typeof v === 'string' && v) return v;
    if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  }
  return e.message;
}

export function KnowledgeGapsPage() {
  const apiRef = useGridApiRef();
  const { hasPerm } = usePermissions();
  const canDefine = hasPerm('console_ai_vocabulary_manage');
  const presets = useGridPresets({
    grid: 'knowledge_gaps',
    defaultOrder: FIELD_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    filters: PRESET_FILTERS,
    // The order is the backend's (`ordering`, in the URL): no grid sort to keep.
    supportsSort: false,
  });
  const {
    extra,
    getParam,
    setParam,
    setParams,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const group = groupOf(getParam('group'));
  const since = getParam('since') ?? '';
  const until = getParam('until') ?? '';
  const kinds = (getParam('kind') ?? '').split(',').filter(Boolean);
  const ordering = orderingFor(group, getParam('ordering'));

  // The backend's window rule, said before asking.
  const days = since ? windowDays(since, until || todayUtc()) : null;
  const windowProblem =
    days !== null && days < 1
      ? 'The first day is after the last one.'
      : days !== null && days > MAX_WINDOW_DAYS
        ? `A window is at most ${MAX_WINDOW_DAYS} days; this one is ${days}.`
        : null;

  const [extendedTimeout, setExtendedTimeout] = useState(false);
  // `ordering` as the group sorts on (a hand-edited or stale one is dropped, never sent).
  const params = useMemo(() => {
    const out = { ...(extra as gapsApi.KnowledgeGapsParams) };
    delete out.ordering;
    return ordering ? { ...out, ordering } : out;
  }, [extra, ordering]);
  const identity = JSON.stringify(params);
  useEffect(() => setExtendedTimeout(false), [identity]);

  const autoRefresh = useGridAutoRefresh({ grid: 'knowledge_gaps' });
  const query = useQuery({
    queryKey: [...knowledgeGapKeys.gaps(params), extendedTimeout],
    queryFn: () =>
      gapsApi.knowledgeGaps({
        ...params,
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking && windowProblem === null,
  });
  // A previous group's rows have other columns: show none until this group's arrive.
  const data = windowProblem === null && query.data?.group === group ? query.data : undefined;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const kindInfo = useMemo(
    () => new Map<string, KnowledgeGapKind>((query.data?.kinds ?? []).map((k) => [k.key, k])),
    [query.data],
  );
  const kindName = (key: string) => kindInfo.get(key)?.name ?? key;
  const refused = refusalText(query.error);

  const sortable = ORDERING_KEYS[group];
  const sortModel = useMemo<GridSortModel>(
    () => [
      ordering
        ? { field: ordering.replace(/^-/, ''), sort: ordering.startsWith('-') ? 'desc' : 'asc' }
        : { field: 'count', sort: 'desc' },
    ],
    [ordering],
  );
  const onSortModelChange = (model: GridSortModel) => {
    const m = model[0];
    if (!m || !m.sort || !sortable.includes(m.field)) return setParam('ordering', null);
    const next = `${m.sort === 'desc' ? '-' : ''}${m.field}`;
    setParam('ordering', next === '-count' ? null : next);
  };
  const setGroup = (next: KnowledgeGapGroup) =>
    setParams({
      group: next === 'subject' ? null : next,
      ordering: orderingFor(next, getParam('ordering')) ?? null,
    });

  const columns = useMemo<GridColDef<KnowledgeGapRow>[]>(() => {
    const all: GridColDef<KnowledgeGapRow>[] = [
      { field: 'day', headerName: 'Day (UTC)', width: 120 },
      {
        field: 'tenant',
        headerName: 'Tenant',
        width: 170,
        valueGetter: (_, row) =>
          row.tenant?.code ?? (row.tenant_id == null ? '' : `#${row.tenant_id}`),
        renderCell: (p) =>
          p.row.tenant ? (
            <WithTenantMark environment={p.row.tenant.environment}>
              <ValueActions actions={operatorActions(p.row.tenant, tenantGrid(p.row.tenant.id))}>
                {p.row.tenant.code || p.row.tenant.name}
              </ValueActions>
            </WithTenantMark>
          ) : p.row.tenant_id == null ? (
            <Typography variant="body2" component="span" color="text.secondary">
              No tenant
            </Typography>
          ) : (
            `#${p.row.tenant_id}`
          ),
      },
      {
        field: 'kind',
        headerName: 'Kind',
        width: 250,
        valueGetter: (_, row) => kindName(row.kind),
        renderCell: (p) => (
          <Tooltip title={kindInfo.get(p.row.kind)?.description ?? ''} describeChild>
            <span>
              <ValueActions actions={kindActions(p.row.kind)}>{kindName(p.row.kind)}</ValueActions>
            </span>
          </Tooltip>
        ),
      },
      {
        field: 'subject',
        headerName: 'Asked',
        flex: 1,
        minWidth: 220,
        renderCell: (p) =>
          p.row.withheld ? (
            <Chip
              size="small"
              variant="outlined"
              label={WITHHELD_LABELS[p.row.withheld]}
              sx={{ fontStyle: 'italic' }}
            />
          ) : (
            <ValueActions actions={subjectActions(p.row)}>
              <UntrustedInline text={p.row.subject} />
            </ValueActions>
          ),
      },
      {
        field: 'detail',
        headerName: 'Detail',
        width: 180,
        renderCell: (p) => <UntrustedInline text={p.row.detail} empty="" />,
      },
      {
        field: 'count',
        headerName: 'Times',
        width: 100,
        type: 'number',
        valueFormatter: (value: number) => count.format(value),
      },
      {
        field: 'days',
        headerName: 'Days seen',
        width: 110,
        type: 'number',
      },
      {
        field: 'tenants',
        headerName: 'Tenants',
        width: 100,
        type: 'number',
      },
      { field: 'first_day', headerName: 'First day', width: 120 },
      { field: 'last_day', headerName: 'Last day', width: 120 },
      {
        field: 'last_at',
        headerName: 'Last seen',
        width: 160,
        renderCell: (p) => (
          <span title={formatRelativeTime(p.row.last_at)}>{formatDate(p.row.last_at)}</span>
        ),
      },
      {
        field: 'define',
        headerName: 'Vocabulary',
        width: 150,
        sortable: false,
        disableExport: true,
        renderCell: (p) => {
          const href = canDefine ? defineHref(p.row) : null;
          return href ? (
            <Link component={RouterLink} to={href} underline="hover">
              Define this word
            </Link>
          ) : null;
        },
      },
    ];
    return all
      .filter((c) => GROUP_FIELDS[group](c.field))
      .map((c) => ({ ...c, sortable: sortable.includes(c.field) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, kindInfo, canDefine]);

  const kindOptions = KNOWLEDGE_GAP_KINDS.map((k) => ({ value: k, label: kindName(k) }));

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'since',
      priority: 5,
      active: Boolean(since),
      node: (
        <TextField
          size="small"
          type="date"
          label="From (UTC)"
          InputLabelProps={{ shrink: true }}
          value={since}
          onChange={(e) => setParam('since', e.target.value || null)}
          sx={{ width: 170 }}
        />
      ),
    },
    {
      key: 'until',
      priority: 5,
      active: Boolean(until),
      node: (
        <TextField
          size="small"
          type="date"
          label="To (UTC)"
          InputLabelProps={{ shrink: true }}
          value={until}
          onChange={(e) => setParam('until', e.target.value || null)}
          sx={{ width: 170 }}
        />
      ),
    },
    {
      key: 'tenant',
      priority: 4,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 260 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'kind',
      priority: 3,
      active: kinds.length > 0,
      node: (
        <TextField
          select
          size="small"
          label="Kind"
          value={kinds}
          onChange={(e) =>
            setParam('kind', (e.target.value as unknown as string[]).join(',') || null)
          }
          sx={{ width: 260 }}
          SelectProps={{
            multiple: true,
            renderValue: (sel) => (sel as string[]).map(kindName).join(', '),
          }}
        >
          {kindOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Checkbox size="small" checked={kinds.includes(o.value)} sx={{ py: 0 }} />
              <ListItemText primary={o.label} secondary={o.value} />
            </MenuItem>
          ))}
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="knowledge-gaps"
        title="Knowledge gaps"
        subtitle="What clients’ AI could not resolve — words it looked up in vain, variants it had to ask for, metrics and breakdowns we lack, re-asks — per subject, tenant or UTC day."
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={group}
          onChange={(_, v: KnowledgeGapGroup | null) => v && setGroup(v)}
          aria-label="Group the gaps"
        >
          {(Object.keys(GROUP_LABELS) as KnowledgeGapGroup[]).map((g) => (
            <ToggleButton key={g} value={g}>
              {GROUP_LABELS[g]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {windowProblem ? null : (
          <Typography variant="body2" color="text.secondary" data-window>
            {[
              data &&
                `UTC days ${data.since} to ${data.until} · ${rows.length} row${rows.length === 1 ? '' : 's'}.`,
              `By default the last 30 days, at most ${MAX_WINDOW_DAYS}.`,
            ]
              .filter(Boolean)
              .join(' ')}
          </Typography>
        )}
      </Box>

      {windowProblem && (
        <Alert severity="warning" sx={{ mb: 2 }} data-window-problem>
          {windowProblem}
        </Alert>
      )}
      {data?.truncated && (
        <Alert severity="warning" sx={{ mb: 2 }} data-truncated>
          More rows than the 1000 shown — narrow the days, the tenants, the kinds or search.
        </Alert>
      )}
      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}
      {refused && windowProblem === null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {refused}
        </Alert>
      )}

      <UntrustedLegend title="Clients’ AI’s words" legend={LEGEND} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<KnowledgeGapRow>
          apiRef={apiRef}
          rows={rows}
          getRowId={rowId}
          columns={orderColumns(columns, presets.orderedFields)}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          disableMultipleColumnsSorting
          pagination
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          pageSizeOptions={[25, 50, 100]}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          disableRowSelectionOnClick
          disableColumnFilter
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {windowProblem
                  ? 'Fix the window to see the knowledge gaps.'
                  : 'No knowledge gap in this window.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, {
                paused: extendedTimeout,
                disabled: windowProblem !== null,
              }),
              hideFilterButton: true,
              onResetAllFilters: resetAllFilters,
              resetDisabled: !anyFilterActive,
              search: true,
              searchPlaceholder: 'Part of a subject or a detail',
              searchValue: getParam('search') ?? '',
              onSearchChange: (v: string) => setParam('search', v.trim() ? v.slice(0, MAX_SEARCH) : null),
              searchOnSubmit: true,
              exportFileName: 'knowledge-gaps',
              presets: presets.toolbar,
            } as GridToolbarProps,
          }}
        />
      )}
    </Box>
  );
}
